import { useState, useCallback, useRef } from 'react';

export interface ExpertChatScope {
  period?: string;
  dateFrom?: string;
  dateTo?: string;
  cohortIds?: number[];
  countryCodes?: string[];
  /** Display label ("Last 180 days · All cohorts · AU") stored with the message. */
  label?: string;
  /** When the underlying BE calculation ran, for the conversation stamp. */
  calculatedAt?: string;
}

interface StreamingChatParams {
  expertType: string;
  message: string;
  conversationId?: string;
  brandId?: number;
  jwtToken: string;
  responseStyle?: 'brief' | 'detailed';
  /** Data-selection scope so the expert reasons over the window on screen. */
  scope?: ExpertChatScope;
}

interface StreamingChatResult {
  conversationId: string;
  messageId: string;
  sources: any[];
  metadata: {
    processingTimeMs: number;
    tokensUsed: number;
    model: string;
    confidence: number;
    usedFallback?: boolean;
    provider?: string;
  };
}

interface UseStreamingChatReturn {
  streamMessage: (params: StreamingChatParams) => Promise<StreamingChatResult | null>;
  streamingContent: string;
  isStreaming: boolean;
  error: string | null;
  abortStream: () => void;
}

/**
 * Custom hook for streaming AI expert chat responses via SSE
 */
export const useStreamingChat = (): UseStreamingChatReturn => {
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const abortStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  const streamMessage = useCallback(
    async (params: StreamingChatParams): Promise<StreamingChatResult | null> => {
      const { expertType, message, conversationId, brandId, jwtToken, responseStyle, scope } = params;

      // Clean up any existing stream
      abortStream();

      // Reset state
      setStreamingContent('');
      setError(null);
      setIsStreaming(true);

      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '') ||
        'http://localhost:3501';

      // Build URL with query parameters
      const url = new URL(`${apiBaseUrl}/api/ai-expert/chat/stream`);
      url.searchParams.set('expertType', expertType);
      url.searchParams.set('message', message);
      if (conversationId) {
        url.searchParams.set('conversationId', conversationId);
      }
      if (brandId) {
        url.searchParams.set('brandId', brandId.toString());
      }
      if (responseStyle) {
        url.searchParams.set('responseStyle', responseStyle);
      }
      if (scope) {
        url.searchParams.set('scope', JSON.stringify(scope));
      }

      return new Promise((resolve, reject) => {
        // Create abort controller for timeout
        abortControllerRef.current = new AbortController();

        // Use fetch with SSE handling since EventSource doesn't support auth headers
        fetch(url.toString(), {
          method: 'GET',
          headers: {
            Accept: 'text/event-stream',
            Authorization: `Bearer ${jwtToken}`,
            'Cache-Control': 'no-cache',
          },
          signal: abortControllerRef.current.signal,
        })
          .then(async (response) => {
            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(errorText || `HTTP error ${response.status}`);
            }

            const reader = response.body?.getReader();
            if (!reader) {
              throw new Error('No response body');
            }

            const decoder = new TextDecoder();
            let buffer = '';
            let result: StreamingChatResult | null = null;

            const processLine = (line: string) => {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                if (data === '[DONE]') {
                  return;
                }

                try {
                  const parsed = JSON.parse(data);

                  switch (parsed.type) {
                    case 'start':
                      // Stream started
                      break;
                    case 'chunk':
                      // Append content chunk
                      setStreamingContent((prev) => prev + parsed.content);
                      break;
                    case 'done':
                      // Stream completed
                      result = {
                        conversationId: parsed.conversationId,
                        messageId: parsed.messageId,
                        sources: parsed.sources || [],
                        metadata: parsed.metadata || {},
                      };
                      break;
                    case 'error':
                      setError(parsed.error);
                      reject(new Error(parsed.error));
                      break;
                  }
                } catch (e) {
                  // Ignore parse errors for non-JSON data
                }
              }
            };

            // Read the stream
            while (true) {
              const { done, value } = await reader.read();

              if (done) {
                // Process any remaining buffer
                if (buffer) {
                  buffer.split('\n').forEach(processLine);
                }
                break;
              }

              // Decode and add to buffer
              buffer += decoder.decode(value, { stream: true });

              // Process complete lines
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // Keep incomplete line in buffer

              lines.forEach(processLine);
            }

            setIsStreaming(false);

            if (result) {
              resolve(result);
            } else {
              reject(new Error('Stream ended without completion'));
            }
          })
          .catch((err) => {
            if (err.name === 'AbortError') {
              setIsStreaming(false);
              resolve(null);
            } else {
              setError(err.message);
              setIsStreaming(false);
              reject(err);
            }
          });
      });
    },
    [abortStream]
  );

  return {
    streamMessage,
    streamingContent,
    isStreaming,
    error,
    abortStream,
  };
};
